import axios from "axios"
import useSWR from "swr"

import { Datasets } from "../../../constants/enums"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected, getClientPublic } from "../clients/clients"
import {
    ISingleDevice,
    singleDeviceMapper,
} from "../mappers/singleDeviceMapper"
import { SINGLE_DEVICE } from "../urls/hybrid"

export interface IUseMultiFetcherGetDevice {
    data: ISingleDevice | undefined
    isLoading: boolean
    isError: boolean
    mutate: () => void
    isValidating: boolean
}

/**
 * Hook for getting a single generation device. Checks authentication status
 * to determine if public / protected API should be used. Public users should
 * not be directed to the protected routes. If auth status is configuring,
 * the request should be skipped. Once authentication is complete the hook
 * will rerender, at which point an API request will be made.
 *
 * This hook has a conditional fetcher, which routes requests through
 * either the public or protected axios client based on auth status and role.
 * For authenticated users with protected access, the hook first attempts the
 * protected endpoint. If the request fails with a 403 (Forbidden) due to
 * granular permission restrictions, or a 404 (Not Found) because the device
 * does not exist in the protected dataset, it falls back to the public endpoint.
 *
 * The request is deferred if the auth status is `isConfiguring`.
 * Therefore, the `isLoading` output value is augmented with this
 * parameter for this hook.
 *
 * @param {string} deviceId - the backend ID of the device to be returned
 * @param {Datasets} dataset - the type of dataset to be searched for `deviceId`
 * @returns {IUseMultiFetcherGetDevice} - SWR data, loading and error states.
 */
const useMultiFetcherGetDevice = (
    deviceId: string,
    dataset: Datasets
): IUseMultiFetcherGetDevice => {
    const { isSignedIn, isConfiguring, groups } = useGetUserData()
    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        isConfiguring
            ? null
            : SINGLE_DEVICE(deviceId, dataset, canAccessProtected),
        async (): Promise<ISingleDevice> => {
            const protectedUrl = SINGLE_DEVICE(deviceId, dataset, true)
            const publicUrl = SINGLE_DEVICE(deviceId, dataset, false)

            // If user cannot access protected routes, use public directly
            if (!canAccessProtected) {
                const res = await getClientPublic(publicUrl)
                return singleDeviceMapper.mapDevice(res, dataset)
            }

            // Try protected first, fallback to public on 403 or 404
            try {
                const res = await getClientProtected(protectedUrl)
                return singleDeviceMapper.mapDevice(res, dataset)
            } catch (protectedError) {
                if (
                    axios.isAxiosError(protectedError) &&
                    (protectedError.response?.status === 403 ||
                        protectedError.response?.status === 404)
                ) {
                    const res = await getClientPublic(publicUrl)
                    return singleDeviceMapper.mapDevice(res, dataset)
                }
                throw protectedError
            }
        },
        { keepPreviousData: true }
    )
    return {
        data,
        isLoading: isLoading || isConfiguring, // See notes in docstring
        isError: error,
        mutate,
        isValidating,
    }
}

export default useMultiFetcherGetDevice
